import React from 'react';
import { useNavigate } from 'react-router-dom';
import { logger } from '../utils/logger';
import {
  Box,
  Typography,
  Button,
  Chip,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Visibility as ViewIcon,
  ExitToApp as ExitIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import { usePerspective } from '../contexts/PerspectiveContext';

/**
 * PerspectiveIndicator Component
 * Displays a banner/indicator when an admin is viewing from a client/rep perspective
 * Shows who they're viewing as and provides a way to return to admin view
 */
const PerspectiveIndicator = () => {
  const navigate = useNavigate();
  const {
    isInSwitchedView,
    isLoading,
    perspectiveContext,
    returnToAdminView,
    getPerspectiveDisplayInfo,
    clearPerspective
  } = usePerspective();

  const [isReturning, setIsReturning] = React.useState(false);

  // Check if user is authenticated
  const authToken = localStorage.getItem('authToken');
  const currentUser = sessionStorage.getItem('barebiologics_currentUser');

  // If not authenticated, clear any stale perspective state and don't render
  React.useEffect(() => {
    if (!authToken && isInSwitchedView) {
      logger.log('PerspectiveIndicator: No auth token, clearing stale perspective');
      clearPerspective();
    }
  }, [authToken, isInSwitchedView, clearPerspective]);

  // Don't render if not authenticated or not in switched view
  if (!authToken || !currentUser || !isInSwitchedView || !perspectiveContext) {
    return null;
  }

  const displayInfo = getPerspectiveDisplayInfo();

  const handleReturnToAdmin = async () => {
    setIsReturning(true);
    try {
      const result = await returnToAdminView();
      if (result.success) {
        // Navigate back to admin view using React Router to preserve state
        logger.log('PerspectiveIndicator: Returning to admin view');
        navigate('/internal/epl/client-management');
      }
    } catch (error) {
      logger.error('Failed to return to admin view:', error);
    } finally {
      setIsReturning(false);
    }
  };

  const getPerspectiveColor = () => {
    switch (perspectiveContext.currentPerspective) {
      case 'CLIENT':
        return '#2563eb'; // Blue for client view
      case 'REP':
        return '#7c3aed'; // Purple for rep view
      default:
        return '#059669'; // Green for admin
    }
  };

  const getPerspectiveLabel = () => {
    switch (perspectiveContext.currentPerspective) {
      case 'CLIENT':
        return 'Client View';
      case 'REP':
        return 'Rep View';
      default:
        return 'Admin View';
    }
  };

  const color = getPerspectiveColor();

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        backgroundColor: color,
        color: 'white',
        py: 1,
        px: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          maxWidth: '1400px',
          mx: 'auto',
        }}
      >
        {/* Left section - View indicator */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ViewIcon sx={{ fontSize: 20 }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {getPerspectiveLabel()}
            </Typography>
          </Box>

          <Chip
            icon={<PersonIcon sx={{ color: 'white !important', fontSize: 16 }} />}
            label={displayInfo?.targetUserName || 'Unknown User'}
            size="small"
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              fontWeight: 500,
              '& .MuiChip-icon': { color: 'white' },
            }}
          />

          {displayInfo?.targetUserEmail && (
            <Typography
              variant="caption"
              sx={{ opacity: 0.9, display: { xs: 'none', sm: 'block' } }}
            >
              ({displayInfo.targetUserEmail})
            </Typography>
          )}
        </Box>

        {/* Right section - Return button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="You are viewing as another user. Click to return to your admin view.">
            <Chip
              icon={<TimeIcon sx={{ color: 'white !important', fontSize: 14 }} />}
              label="Troubleshooting Mode"
              size="small"
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.15)',
                color: 'white',
                fontSize: '0.7rem',
                display: { xs: 'none', md: 'flex' },
              }}
            />
          </Tooltip>

          <Button
            variant="contained"
            size="small"
            startIcon={isReturning ? <CircularProgress size={14} color="inherit" /> : <ExitIcon />}
            onClick={handleReturnToAdmin}
            disabled={isReturning || isLoading}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.3)',
              },
              '&:disabled': {
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.6)',
              },
            }}
          >
            {isReturning ? 'Returning...' : 'Return to Admin'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default PerspectiveIndicator;
